import { Grid } from '@mui/material';
import { ODSColumn, ODSTabElement, ODSTabs } from '@telekom-ods/react-ui-kit';
import { useEffect, useState } from 'react';
import { useParams, useLoaderData } from 'react-router';

import Auth from '../../../../api/auth';
import BreadCrumb from '../../../../components/BreadCrumb';
import useApiQuery from '../../../../hooks/useApiQuery';
import rbacCheck from '../../../../rbac/rbacCheck';
import JuiceGroupContacts from '../../../Reports/JuiceGroupContacts';

import Overview from './Overview';
import './Product.css';
import productDetailsContent from './ProductDetailsContent';

import { useUserData } from '@/redux/selectors';
import type { APIHookArg } from '@/types';
import type { Product } from '@/types/productTypes';

interface ProductDetailsProps {
  productIdProps?: string;
  details?: Product;
}

const ProductDetails = ({ productIdProps, details }: ProductDetailsProps): React.ReactElement => {
  const { productId: productParamsId } = useParams();
  const { makeApiCall: getProductCall } = useApiQuery(
    Auth.fetchV2ProductById as unknown as APIHookArg
  );

  const productId = productIdProps ?? productParamsId;
  const userData = useUserData();

  const loaderProduct: Product = useLoaderData();
  const [product, setProduct] = useState<Product | undefined>(
    productIdProps ? details : loaderProduct
  );

  const [activeResources, setActiveResources] = useState<boolean>(false);

  const canViewContacts = rbacCheck(userData.roles, '/products:CONTACTS_VIEW');

  const canSeeOrganizations = rbacCheck(userData.roles, '/organizations:VIEW_ALL');

  const getProduct = async (): Promise<void> => {
    const data = (await getProductCall(productId)) as Product | undefined;
    if (data) setProduct(data);
  };

  useEffect(() => {
    if (productIdProps) {
      setProduct(details);
    }
  }, [productIdProps, details]);

  const renderContent = (): React.ReactElement[] => {
    const components = product?.type
      ? productDetailsContent[product.type as keyof typeof productDetailsContent]
      : [];
    return components.map(content => {
      const { Component } = content;
      return (
        <ODSTabElement
          key={content.name}
          text={content.text}
        >
          {product && (
            <Component
              setActiveResources={setActiveResources}
              product={product}
            />
          )}
        </ODSTabElement>
      );
    });
  };

  return (
    <Grid
      container
      className='content-with-header'
      direction='column'
      rowGap={2}
    >
      <Grid
        item
        alignSelf='flex-start'
      >
        {productParamsId && (
          <BreadCrumb
            items={[
              {
                href: canSeeOrganizations
                  ? '/governance/organizations'
                  : `/governance/organizations/${product?.organizationId ?? ''}`,
                text: 'organizations',
              },
              {
                href: `/governance/organizations/${product?.organizationId ?? ''}`,
                text: product?.organization?.enterpriseName ?? '',
              },
              {
                href: `/governance/organizations/${product?.organizationId ?? ''}/projects`,
                text: 'projects',
              },
              {
                href: `/projects/${product?.project._id ?? ''}`,
                text: product?.project.name ?? '',
              },
              {
                href: `/projects/${product?.project._id ?? ''}`,
                text: 'products',
              },
              {
                href: '#',
                text: product?.name ?? '',
              },
            ]}
          />
        )}
      </Grid>
      <Grid item>
        <div style={{ marginBottom: '2rem' }}>
          {product && (
            <Overview
              product={product}
              activeResources={activeResources}
              onChange={() => {
                void getProduct();
              }}
            />
          )}
        </div>
        <ODSTabs
          className='ODS-tabs'
          divider
        >
          {renderContent()}
          {canViewContacts && (
            <ODSTabElement text='Contacts'>
              <ODSColumn style={{ width: '100%' }}>
                {product?.details.juiceGroup && (
                  <JuiceGroupContacts
                    allowSelection={true}
                    juiceGroup={product.details.juiceGroup}
                  />
                )}
              </ODSColumn>
            </ODSTabElement>
          )}
        </ODSTabs>
      </Grid>
    </Grid>
  );
};

export default ProductDetails;
